'use client'
import classNames from 'classnames'

import React, { useEffect, useState } from 'react'

import Image from 'next/image'
import { useTranslation } from 'app/i18n/client'

import { formatToYen, padNumber, formatDate } from 'utils'

import {
  Button,
  Loading,
  Checkbox,
  Select,
  ReferencePrice,
} from 'app/components/atoms'

import useAuthentication from 'app/hooks/useAuthentication'

import ArrowRightIcon from 'public/icons/arrow-right.svg'
import HouseIcon from 'public/icons/house-lg.svg'
import UserIcon from 'public/icons/user.svg'
import KidIcon from 'public/icons/kid.svg'

import useQueryRoomPrice from 'app/hooks/useQueryRoomPrice'
import useWindowDimensions from 'app/hooks/useWindowDimensions'

import HotelPlanCardProps from './HotelPlanCard.props'
import { getRoomOptions, getPeopleOptions } from './utils'
import { useParams } from 'next/navigation'

const HotelPlanCard: React.FunctionComponent<HotelPlanCardProps> = ({
  id,
  type = 'plan',
  isMultipleSelect,
  canEdit,
  canEditRoomNumber = false,
  canSelect = canEdit,
  hotelCode,
  maxRooms,
  minPeople,
  maxPeople,
  image,
  title,
  subTitle,
  amenitiesList = '',
  checkIn,
  checkOut,
  roomPlanCode,
  membershipPromotionId,
  roomNumber,
  adultNumber,
  childrenNumber,
  recommended,
  selected,
  membershipPrice,
  regularPrice,
  referenceCurrency,
  referenceRegularPriceCents,
  referenceMembershipPriceCents,
  onViewDetail,
  onReserve,
  onEdit,
  onSelect,
  onUpdatePrice,
}) => {
  const [isUpdatedGuestNumber, setIsUpdatedGuestNumber] = useState(false)
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const { isAuthenticated } = useAuthentication()
  const { width } = useWindowDimensions()

  const [isViewingDetail, setIsViewingDetail] = useState(false)
  const [selectedReserve, setSelectedReserve] = useState<
    'regular' | 'membership'
  >()
  const {
    data: priceData,
    isError: isLoadingPriceError,
    isLoading: isLoadingPrice,
  } = useQueryRoomPrice({
    hotelCode,
    checkin: formatDate(checkIn),
    checkout: formatDate(checkOut),
    rooms: [
      {
        roomPlanCode,
        numberOfAdult: adultNumber,
        numberOfChildren: childrenNumber,
        numberOfRoom: 1,
        membershipPromotionId: membershipPromotionId,
      },
    ],
    enable: isUpdatedGuestNumber,
  })

  useEffect(() => {
    if (priceData) {
      const { originalPriceWithTax, totalPrice } = priceData
      onUpdatePrice(totalPrice, originalPriceWithTax)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceData])

  useEffect(() => {
    if (isLoadingPriceError && selected) {
      onSelect(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPriceError])

  const roomOptions = getRoomOptions(maxRooms)
  const adultOptions = getPeopleOptions(maxPeople, minPeople)
  const childrenOptions = getPeopleOptions(adultNumber, 0)

  const onChangeRoomNumber = (roomNumber: number) => {
    onEdit &&
      onEdit(Number(roomNumber), Number(adultNumber), Number(childrenNumber))
  }

  const onChangeAdultNumber = (adultNumber: number) => {
    setIsUpdatedGuestNumber(true)
    onEdit &&
      onEdit(
        Number(roomNumber),
        Number(adultNumber),
        Math.min(Number(childrenNumber), Number(adultNumber)),
      )
  }

  const onChangeChildrenNumber = (childrenNumber: number) => {
    setIsUpdatedGuestNumber(true)
    onEdit &&
      onEdit(Number(roomNumber), Number(adultNumber), Number(childrenNumber))
  }

  const handleViewDetail = () => {
    if (onViewDetail) {
      setIsViewingDetail(true)
      onViewDetail()
    }
  }

  const priceList = [
    {
      title: t('Regular price'),
      price: regularPrice * roomNumber,
      referenceAmountCents:
        referenceRegularPriceCents != null
          ? referenceRegularPriceCents * roomNumber
          : undefined,
      isLoading: selectedReserve === 'regular',
      onClick: () => {
        if (onReserve) {
          setSelectedReserve('regular')
          onReserve('regular')
        }
      },
      disabled: isAuthenticated,
    },
    {
      title: t('Membership price'),
      price: membershipPrice * roomNumber,
      referenceAmountCents:
        referenceMembershipPriceCents != null
          ? referenceMembershipPriceCents * roomNumber
          : undefined,
      highlight: true,
      isLoading: selectedReserve === 'membership',
      onClick: () => {
        if (onReserve) {
          setSelectedReserve('membership')
          onReserve('membership')
        }
      },
      disabled: false,
    },
  ]

  const RenderImage = () => {
    const height = `${((width - 72) * 3) / 4}px`
    return (
      <div
        className={classNames(
          'lg:px-0 mx-4 mt-2 lg:m-auto lg:ml-6 w-auto relative lg:flex-none lg:h-54',
          {
            'lg:w-60': type === 'plan',
            'lg:w-58': type === 'roomPlan',
          },
          width <= 768 ? `h-[${height}]` : '',
        )}
      >
        {image && (
          <Image
            alt={t('Hotel Image')}
            src={image}
            fill
            sizes="100vw"
            style={{
              objectFit: 'fill',
            }}
          />
        )}
        {recommended && (
          <div className="lg:hidden absolute left-0 top-0">
            <span className="py-1 px-2 bg-primary-light text-gray-dark text-sm uppercase w-auto">
              {t('recommended')}
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'mt-10 lg:mt-0 w-full bg-white pt-2 lg:flex lg:space-x-5 text-left items-stretch lg:h-66',
        `${isMultipleSelect ? 'lg:pt-0' : 'lg:py-0'}`,
        { 'pb-4 lg:pb-0 lg:pr-4': !canSelect },
      )}
    >
      <RenderImage />
      <div className="mt-4 lg:mt-0 px-4 lg:px-0 justify-center flex flex-col flex-1">
        <div
          className={classNames(
            'font-lora lg:line-clamp-4',
            type === 'roomPlan' ? 'text-lg' : 'text-2xl',
          )}
        >
          {title}
        </div>
        <div
          className={classNames(
            'text-secondary mt-2 line-clamp-3',
            type === 'roomPlan' ? 'text-xs' : 'lg:mt-4 text-base',
          )}
        >
          {subTitle}
        </div>
        <div
          className={classNames(
            'mt-2 lg:mt-1 flex flex-wrap space-x-4 text-gray-light',
            type === 'roomPlan' ? 'text-xs' : 'text-sm',
          )}
        >
          {amenitiesList}
        </div>
        <div
          className={classNames(
            'lg:hidden py-4 border-gray-150 border-b lg:border-b-0',
            { hidden: isMultipleSelect || type !== 'plan' },
          )}
        >
          {isViewingDetail ? (
            <div className="relative text-left py-2.5 flex mt-2 w-24 justify-center">
              <Loading
                className="!text-white"
                baseClassName="!text-primary"
                size="sm"
              />
            </div>
          ) : (
            <a
              onClick={handleViewDetail}
              className="mt-2.5 flex text-sm text-primary items-center"
            >
              {t('See details')}
              <ArrowRightIcon className="ml-2.5 fill-current text-gray-500" />
            </a>
          )}
        </div>
        <div
          className={classNames('mt-4 mb-4 lg:mb-0', {
            hidden: isMultipleSelect || type !== 'roomPlan',
          })}
        >
          <Button
            className="!uppercase w-full lg:w-32"
            size="sm"
            loading={isViewingDetail}
            onClick={handleViewDetail}
          >
            {t('Details')}
          </Button>
        </div>
        {recommended && (
          <div
            className={classNames(
              'hidden lg:block border-secondary border-b lg:border-b-0',
              {
                'mt-8': !isMultipleSelect,
                'mt-3': isMultipleSelect,
              },
            )}
          >
            <span className="py-1 px-2 bg-secondary bg-opacity-10 text-gray-dark text-sm uppercase w-auto">
              {t('recommended')}
            </span>
          </div>
        )}
      </div>
      <div
        className={classNames(
          'pb-2 lg:pb-0 px-4 lg:px-0 flex flex-col items-end flex-none',
          isMultipleSelect ? 'justify-center pb-2' : 'justify-center',
          {
            'lg:w-79': type === 'plan',
            'lg:w-60': type === 'roomPlan' && isMultipleSelect,
            'lg:w-76':
              type === 'roomPlan' && !isMultipleSelect && locale !== 'ja',
            'lg:w-68':
              type === 'roomPlan' && !isMultipleSelect && locale === 'ja',
          },
        )}
      >
        <ul
          role="list"
          className={classNames('lg:mt-0 divide-y divide-gray-150 w-full', {
            hidden: !priceList || priceList.length === 0,
            'mt-4': isMultipleSelect,
          })}
        >
          {priceList?.map((item) => (
            <li key={item.title} className="py-2 flex">
              <div className="flex justify-between w-full lg:px-2">
                <div
                  className={classNames('lg:flex lg:items-center', {
                    'flex flex-row-reverse lg:flex-row justify-between w-full lg:w-auto items-center':
                      isMultipleSelect,
                  })}
                >
                  <div className="h-6 lg:hidden">
                    <PriceItem
                      isError={isLoadingPriceError}
                      isLoading={isLoadingPrice}
                      highlight={item?.highlight}
                      price={item?.price}
                    />
                  </div>
                  <div
                    className={classNames('text-xxs text-gray-50', {
                      'space-x-1': locale === 'ja',
                      'flex flex-col space-y-1': locale === 'en',
                    })}
                  >
                    <span className="uppercase font-semibold text-gray-600">
                      {item.title}
                    </span>
                    <span className="text-gray-light">
                      ({t('Including Tax')})
                    </span>
                  </div>
                </div>
                <div className="mt-1 lg:mt-0 hidden lg:flex lg:flex-col lg:items-end lg:flex-1 lg:justify-center flex-none">
                  <PriceItem
                    isError={isLoadingPriceError}
                    isLoading={isLoadingPrice}
                    highlight={item?.highlight}
                    price={item?.price}
                  />
                  <ReferencePrice
                    currency={referenceCurrency}
                    amountCents={item?.referenceAmountCents}
                    className={
                      item?.highlight
                        ? 'text-base font-semibold text-primary'
                        : 'text-xs font-semibold text-gray-dark'
                    }
                  />
                </div>
                <div
                  className={classNames(
                    'flex items-center justify-center ml-5',
                    {
                      'ml-5': type === 'plan',
                      'ml-2': type === 'roomPlan',
                      hidden: isMultipleSelect,
                    },
                  )}
                >
                  <Button
                    className={classNames(
                      '!uppercase text-primary w-32 lg:w-26',
                    )}
                    loadingClassName="!text-primary"
                    loadingSpinerClassName="!text-white"
                    size="sm"
                    disabled={item?.disabled}
                    onClick={item.onClick}
                    loading={item?.isLoading}
                  >
                    {t('Book now')}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {isMultipleSelect && (
          <div>
            <div
              className={classNames(
                'mt-4 lg:mt-0 flex justify-between w-full space-x-4',
                canSelect ? '-lg:px-2' : 'lg:px-2 ',
              )}
            >
              <div className="flex-1 flex flex-col w-full items-center space-y-4 justify-between">
                <HouseIcon />
                {canEdit || canEditRoomNumber ? (
                  <Select
                    containerClassName="w-full"
                    buttonClassName="!py-2.5"
                    placeHolder={t('Select')}
                    options={roomOptions}
                    value={roomNumber}
                    onChanged={onChangeRoomNumber}
                    optionListClassName="w-[130px] min-w-full"
                  />
                ) : (
                  <div className="w-full text-gray-50 h-12 flex items-center justify-center">
                    {padNumber(roomNumber, 2)}
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col w-full items-center space-y-4 justify-between">
                <UserIcon />
                {canEdit ? (
                  <Select
                    containerClassName="w-full"
                    buttonClassName="!py-2.5"
                    placeHolder={t('Select')}
                    options={adultOptions}
                    value={adultNumber}
                    onChanged={onChangeAdultNumber}
                    optionListClassName="w-[130px] min-w-full"
                  />
                ) : (
                  <div className="w-full text-gray-50 h-12 flex items-center justify-center">
                    {padNumber(adultNumber, 2)}
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col w-full items-center space-y-4 justify-between">
                <KidIcon />
                {canEdit ? (
                  <Select
                    containerClassName="w-full"
                    buttonClassName="!py-2.5"
                    placeHolder={t('Select')}
                    options={childrenOptions}
                    value={childrenNumber}
                    onChanged={onChangeChildrenNumber}
                    optionListClassName="w-[130px] min-w-full"
                  />
                ) : (
                  <div className="w-full text-gray-50 h-12 flex items-center justify-center">
                    {padNumber(childrenNumber, 2)}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2.5 text-gray-light text-xs">
              {t(
                'Children over 6 years old will be charged the same fee as adults, so please include them in the number of adults when searching.',
              )}
            </div>
          </div>
        )}
      </div>
      {isMultipleSelect ? (
        <div>
          <div
            className={classNames(
              'mt-5 lg:mt-0 h-20 lg:h-full w-full lg:w-20 flex items-center justify-center bg-white',
              {
                hidden: !canSelect,
              },
            )}
          >
            <Checkbox
              name={`${id}-${roomPlanCode}`}
              containerSize="lg"
              checked={selected}
              onChanged={onSelect}
              disabled={isLoadingPriceError}
            />
          </div>
        </div>
      ) : (
        <div
          className={classNames('hidden w-38 flex-none pr-4', {
            'lg:hidden': type !== 'plan',
            'lg:flex justify-center items-center': type === 'plan',
          })}
        >
          <Button
            size="sm"
            className="!px-8 uppercase !w-full"
            loading={isViewingDetail}
            onClick={handleViewDetail}
          >
            {t('Details')}
          </Button>
        </div>
      )}
    </div>
  )
}

const PriceItem: React.FunctionComponent<{
  isError: boolean
  isLoading: boolean
  highlight?: boolean
  price?: number
}> = ({ isError, isLoading, highlight, price }) => {
  const { t } = useTranslation()

  if (isError)
    return (
      <div className="w-20 py-2 flex-1 text-right text-xs">
        {t('Sorry there is no availability')}
      </div>
    )

  if (isLoading)
    return (
      <div className="w-20 py-2">
        <Loading className="text-white text-opacity-40" size="sm" />
      </div>
    )

  return (
    <div
      className={classNames('font-lora font-semibold', {
        'text-primary': highlight,
      })}
    >
      {formatToYen(price)}
    </div>
  )
}

export default HotelPlanCard
